import React, { useState, useEffect } from 'react';
import { Archive, RotateCcw, Trash2, Search, Calendar, User, AlertCircle } from 'lucide-react';

interface ArchivedDeal {
  deal_id: string;
  business_name: string;
  business_alias?: string;
  description?: string;
  creator: string;
  status: string;
  deal_value?: number;
  currency: string;
  created_at: string;
  updated_at: string;
  archived_at: string;
  archived_by: string;
  archive_reason?: string;
  can_restore: boolean;
}

interface ArchivedDealsPageProps {
  archivedDeals: ArchivedDeal[];
  onRestore: (dealId: string) => Promise<void>;
  onDelete: (dealId: string) => Promise<void>;
  isLoading?: boolean;
}

export default function ArchivedDealsPage({
  archivedDeals,
  onRestore,
  onDelete,
  isLoading = false
}: ArchivedDealsPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDeals, setFilteredDeals] = useState<ArchivedDeal[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<ArchivedDeal | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Filter deals based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = archivedDeals.filter(deal =>
        deal.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (deal.business_alias && deal.business_alias.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (deal.description && deal.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredDeals(filtered);
    } else {
      setFilteredDeals(archivedDeals);
    }
  }, [searchTerm, archivedDeals]);

  const handleRestore = async (dealId: string) => {
    setIsProcessing(true);
    try {
      await onRestore(dealId);
    } catch (error) {
      console.error('Error restoring deal:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (dealId: string) => {
    setIsProcessing(true);
    try {
      await onDelete(dealId);
      setShowDeleteConfirm(false);
      setSelectedDeal(null);
    } catch (error) {
      console.error('Error deleting deal:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Archived Deals</h1>
          <p className="text-gray-600">
            Manage deals that have been archived. You can restore or permanently delete them.
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Archive className="h-4 w-4" />
          <span>{filteredDeals.length} archived deals</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search archived deals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Deals List */}
      {filteredDeals.length === 0 ? (
        <div className="text-center py-12">
          <Archive className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No archived deals</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'No deals match your search criteria.' : 'No deals have been archived yet.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredDeals.map((deal) => (
            <div
              key={deal.deal_id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {deal.business_name}
                    </h3>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Archived
                    </span>
                  </div>
                  
                  {deal.business_alias && (
                    <p className="text-sm text-gray-600 mb-2">
                      Alias: {deal.business_alias}
                    </p>
                  )}
                  
                  {deal.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {deal.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">Value:</span>
                      <span>
                        {deal.deal_value 
                          ? formatCurrency(deal.deal_value, deal.currency)
                          : 'Not specified'
                        }
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">Created:</span>
                      <span>{formatDate(deal.created_at)}</span>
                    </div>
                  </div>

                  {/* Archive Info */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                      <Calendar className="h-4 w-4" />
                      <span>Archived on {formatDate(deal.archived_at)}</span>
                    </div>
                    {deal.archive_reason && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Reason:</span> {deal.archive_reason}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  {deal.can_restore && (
                    <button
                      onClick={() => handleRestore(deal.deal_id)}
                      disabled={isProcessing}
                      className="flex items-center space-x-2 px-3 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span>Restore</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setSelectedDeal(deal);
                      setShowDeleteConfirm(true);
                    }}
                    disabled={isProcessing}
                    className="flex items-center space-x-2 px-3 py-2 text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedDeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="h-6 w-6 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Permanently Delete Deal
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                Are you sure you want to permanently delete <strong>{selectedDeal.business_name}</strong>? 
                This action cannot be undone and will remove all associated data.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedDeal(null);
                  }}
                  disabled={isProcessing}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(selectedDeal.deal_id)}
                  disabled={isProcessing}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {isProcessing ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
